import { Beverage, MealCategory, ScheduledMeal, Supply } from '../../entities';
import { type ServeType, type ServiceType } from '../../types';
import { LocalDate } from '../../utils/local-date';

export interface BeverageOptionsDto {
  id: number;
  name: string;
  description: string | null;
  price: number | null;
  isExtra: boolean;
  isTestable: boolean;
}

export interface SupplyOptionsDto {
  id: number;
  name: string;
  description: string | null;
  price: number | null;
}

export interface MealCategoryOptionsDto {
  id: number;
  name: string;
  abbreviation: string;
  description: string;
  minQuantityAllowed: number;
  hasDiabeticType: boolean;
  isGeneric: boolean;
  availableWeekDays: string;
  hasSupplies: boolean;
  diets: string;
  preferences: string;
  allowC1OrC2: boolean;
  defaultServiceType: ServiceType;
  defaultServeType: ServeType;
  isPrepackable: boolean;
  beverageFactor: number;
  isCompleteMeal: boolean;
  units: number;
  sortOrder: number;
  isTestable: boolean;
}

export interface ScheduledMealOptionsDto {
  id: number;
  day: string;
  price: number | null;
  isException: boolean;
  name: string;
  mealItemsNames: string[];
  extraMealItem1: { id: number; name: string } | null;
  extraMealItem2: { id: number; name: string } | null;
  extraMealItemServeType1: ServeType | null;
  extraMealItemServeType2: ServeType | null;
  mealCategory: MealCategoryOptionsDto;
  beverages: BeverageOptionsDto[];
  extraBeverages: BeverageOptionsDto[];
  supplies: SupplyOptionsDto[];
}

export interface SiteOrderOptionsDto {
  meals: ScheduledMealOptionsDto[];
}

export const toBeverage = (item: BeverageOptionsDto): Beverage =>
  Object.assign(new Beverage(), {
    id: String(item.id),
    name: item.name,
    description: item.description ?? '',
    price: item.price ?? 0,
    isExtra: item.isExtra,
    isTestable: item.isTestable,
  });

export const toSupply = (item: SupplyOptionsDto): Supply =>
  Object.assign(new Supply(), {
    id: String(item.id),
    name: item.name,
    description: item.description ?? '',
    price: item.price ?? 0,
  });

const toMealCategory = (item: MealCategoryOptionsDto | null): MealCategory =>
  Object.assign(new MealCategory(), {
    ...(item ?? {}),
    id: item ? String(item.id) : undefined,
  });

// TODO: Update this funtion to use one ScheduledMeal structure after migrating from JSON:API
export const toScheduledMeal = (item: ScheduledMealOptionsDto): ScheduledMeal =>
  Object.assign(new ScheduledMeal(), {
    id: String(item.id),
    day: new LocalDate(item.day),
    price: item.price ?? 0,
    isException: item.isException,
    name: item.name,
    mealItemsNames: item.mealItemsNames,
    extraMealItem1: item.extraMealItem1
      ? { id: String(item.extraMealItem1.id), name: item.extraMealItem1.name }
      : null,
    extraMealItem2: item.extraMealItem2
      ? { id: String(item.extraMealItem2.id), name: item.extraMealItem2.name }
      : null,
    extraMealItemServeType1: item.extraMealItemServeType1,
    extraMealItemServeType2: item.extraMealItemServeType2,
    mealCategory: toMealCategory(item.mealCategory),
    beverages: item.beverages?.map(toBeverage) ?? [],
    extraBeverages: item.extraBeverages?.map(toBeverage) ?? [],
    supplies: item.supplies?.map(toSupply) ?? [],
  });
